<!--
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
-->

<!--
  Underline tabs (DESIGN.md §6.4): switch *what* the page shows. The active
  label is primary with a 2 px accent underline; counts sit in a neutral badge,
  or a warning one when the tab asks for attention (a review queue).
-->
<script lang="ts">
  import type { IntlString } from '@hcengineering/platform'
  import { createEventDispatcher } from 'svelte'
  import Label from './Label.svelte'

  interface TabItem {
    id: string
    label: IntlString
    count?: number
    attention?: boolean
  }

  export let items: TabItem[] = []
  export let selected: string | undefined = undefined

  const dispatch = createEventDispatcher<{ select: TabItem }>()
</script>

<div class="fz-tabs" role="tablist">
  {#each items as item (item.id)}
    <button
      class="fz-tab"
      class:selected={item.id === selected}
      role="tab"
      aria-selected={item.id === selected}
      on:click={() => {
        selected = item.id
        dispatch('select', item)
      }}
    >
      <Label label={item.label} />
      {#if item.count !== undefined && ((item.count ?? 0) > 0 || item.attention !== true)}
        <span class="fz-tab__count" class:attention={item.attention === true && (item.count ?? 0) > 0}>{item.count}</span>
      {/if}
    </button>
  {/each}
</div>

<style lang="scss">
  .fz-tabs {
    display: flex;
    align-items: stretch;
    gap: var(--fz-space-5);
    min-width: 0;
    height: 100%;
  }
  .fz-tab {
    display: inline-flex;
    align-items: center;
    gap: var(--fz-space-2);
    flex-shrink: 0;
    height: var(--fz-size-toolbar);
    padding: 0;
    color: var(--fz-text-secondary);
    font-size: var(--fz-type-body-size);
    font-weight: 500;
    white-space: nowrap;
    background: none;
    border: none;
    box-shadow: inset 0 -2px 0 transparent;
    cursor: pointer;
    outline: none;
    transition:
      color var(--fz-motion-fast) var(--fz-ease),
      box-shadow var(--fz-motion-fast) var(--fz-ease);

    &:hover {
      color: var(--fz-text-primary);
    }
    &:focus-visible {
      outline: 2px solid var(--fz-focus);
      outline-offset: 2px;
    }
    &.selected {
      color: var(--fz-text-primary);
      box-shadow: inset 0 -2px 0 var(--fz-accent-text);
    }
  }
  .fz-tab__count {
    min-width: 1.125rem;
    padding: 0 var(--fz-space-1);
    font-size: var(--fz-type-caption-size);
    line-height: 1.125rem;
    font-weight: 500;
    text-align: center;
    font-variant-numeric: tabular-nums;
    color: var(--fz-text-secondary);
    background-color: var(--fz-bg-pressed);
    border-radius: 999px;

    &.attention {
      color: var(--fz-warning-fg);
      background-color: var(--fz-warning-bg);
    }
  }
</style>
